import { useEffect, useRef } from 'react';
import Filter from '@/Asset/images/icon/Filters-lines.svg';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import TRC_CONTENT from '@/constants/trcContentType';
import TRC_PAGE_CATEGORIES from '@/TRC/constants/trcCategories';
import TRC_FILE_TYPE from '@/TRC/constants/trcFileType';
import { getAvailableDurations } from '@/component/FilterFunction/getAvailableDuration';
import FilterModal from '@/component/FilterModal';
import api from '@/data/api';
import { useFilters } from '@/v2/hooks/useFilters';

const TopSectionWithFilter = ({ contents, setContents, category, contentType, containsPdf = false }) => {
	const {
		instructors,
		setInstructors,
		products,
		selectedTags,
		selectedDurations,
		selectedLevels,
		selectedInstructors,
		selectedProducts,
		sortby,
		handleFilterChange,
		isFilterModalOpen,
		handleOpenFilterModal,
		handleCloseFilterModal,
		setSelectedTags,
		setSelectedProducts,
		setSelectedDurations,
		setSelectedInstructors,
		setIsFilterModalOpen,
		durationValue,
		setDurationValues
	} = useFilters(TRC_CONTENT.TRC);

	const initialHasVideos = useRef(false);
	const initialInstructorsRef = useRef([]);
	const initialDurationsRef = useRef([]);
	const initialProductsRef = useRef([]);
	const initialFocusRef = useRef([]);

	useEffect(() => {
		if (initialHasVideos.current) return;
		if (!contents?.length) return;

		initialHasVideos.current = contents.some(
			content => content.fileType === TRC_FILE_TYPE.VIDEO
		);
	}, [contents]);

	useEffect(() => {
		const availableDurations = getAvailableDurations(contents);
		setDurationValues(availableDurations);

		if (initialDurationsRef.current.length === 0) {
			initialDurationsRef.current = availableDurations;
		}
	}, [contents]);

	useEffect(() => {
		if (sortby) {
			handleApplyFilter(selectedTags, selectedDurations, selectedLevels, selectedInstructors, selectedProducts, sortby);
		}
	}, [sortby]);

	useEffect(() => {
		if (contents?.length > 0) {
			const filteredInstructors = [
				...new Set(
					contents
						.filter(content => content.instructor && content.instructor.trim() !== '')
						.map(content => content.instructor)
				)
			];

			if (filteredInstructors.length !== instructors.length) {
				setInstructors(filteredInstructors);
			}

			if (initialInstructorsRef.current.length === 0) {
				initialInstructorsRef.current = filteredInstructors;
			}
		}
	}, [contents]);

	const allTags = contents?.flatMap(content => content.tagsDetails || []) ?? [];
	const tagsById = new Map(allTags.map(tag => [tag._id, tag]));
	const uniqueTags = [...tagsById.values()];

	const allProductIds = contents?.flatMap(content => (content.productsData || []).map(p => p._id)) ?? [];

	const uniqueProductIds = [...new Set(allProductIds)];
	const associatedProducts = products?.filter(product => uniqueProductIds.includes(product.id)) ?? [];

	useEffect(() => {
		if (uniqueTags.length > 0 && initialFocusRef.current.length === 0) {
			initialFocusRef.current = uniqueTags;
		}
		if (associatedProducts.length > 0 && initialProductsRef.current.length === 0) {
			initialProductsRef.current = associatedProducts;
		}
	}, [associatedProducts, uniqueTags]);

	const handleApplyFilter = async (tags, duration, level, instructor, products, sortby) => {
		const durationString = Array.isArray(duration) ? duration.join(',') : duration;

		setSelectedDurations(duration);
		setSelectedInstructors(instructor);
		setSelectedProducts(products);
		setSelectedTags(tags);

		const params = {
			type: contentType,
			tags,
			duration: durationString?.toString() ?? '',
			instructor,
			level: level ?? '',
			products,
			sortby
		};
		if (category && category !== TRC_PAGE_CATEGORIES.VIEW_ALL) {
			params.category = category;
		}

		const response = await api.getContent(params);
		setContents(response?.data);
		setIsFilterModalOpen(false);
	};

	return (
		<div className="c-tab_cata-text d-none d-lg-flex">
			<div id="c-mobile_tab_hide" className="c-tab_cata mb-n9">
				<div className="row m-0 c-tab_box">
					<div className="col p-0">
						<div className="c-filter_btn c-filter_btn-lessons">
							<div className="c-filter_btn-one c-filter_btn-tags unique" style={{ cursor: 'pointer', padding: '0' }}>
								<span
									onClick={handleOpenFilterModal}
									className="c-filters-btn"
									style={{
										padding: '0.63rem 0.75rem',
										display: 'flex',
										justifyContent: 'space-between'
									}}
								>
									<img src={Filter} alt="filter icon" />
									Filters
								</span>

								{isFilterModalOpen && (
									<FilterModal
										isOpen={isFilterModalOpen}
										tags={initialFocusRef.current.length > 0 ? initialFocusRef.current : uniqueTags}
										durationValue={initialDurationsRef.current.length > 0 ? initialDurationsRef.current : durationValue}
										checkedTags={selectedTags}
										checkedDuration={selectedDurations}
										checkedLevels={selectedLevels}
										handleApplyFilter={handleApplyFilter}
										handleClose={handleCloseFilterModal}
										instructors={initialInstructorsRef.current.length > 0 ? initialInstructorsRef.current : instructors}
										checkedInstructors={selectedInstructors}
										products={initialProductsRef.current.length > 0 ? initialProductsRef.current : associatedProducts}
										checkedProducts={selectedProducts}
										showDurationFilter={initialHasVideos.current}
										showLevelFilter={false}
										contentType={TRC_CONTENT.TRC}
									/>
								)}
							</div>

							<div className="c-filter_btn-one">
								<ImportExportIcon size={24} />
								<span>Sort by</span>
								<select className="filterby" onChange={handleFilterChange} value={sortby}>
									<option value="latest">Newest</option>
									<option value="oldest">Oldest</option>
									<option value="title_asc">Title (A - Z)</option>
									<option value="title_desc">Title (Z - A)</option>
									{!containsPdf && (
										<>
											<option value="long_duration">Longest</option>
											<option value="short_duration">Shortest</option>
										</>
									)}
								</select>
							</div>
						</div>
					</div>
				</div>
			</div>
			<h4>{category}</h4>
		</div>
	);
};

export default TopSectionWithFilter;
